/**
 * Global OneDoC Listener
 *
 * Listens for DoC MS Customer UI (oneDoCSPA) postMessage callbacks and
 * persists consent data to sessionStorage under 'ow_aem_dataUsageScopes'.
 *
 * This script is initialized before OneDoC SPA loads. The SPA sends
 * messages via window.postMessage which this listener captures.
 *
 * Handles all callback use-cases:
 *   - completed = 'spaCompletedCallback'
 *   - failed    = 'spaErrorCallback'
 *   - skipped   = 'spaSkippedCallback'
 *
 * Works for both guest and logged-in users.
 */

import { ME_SESSION_STORAGE_KEYS } from "./dependencies/mesessionstoragekeys.es6";
import { DEBUG_SESSION_KEY , logIfDebugEnabled } from "./dependencies/utils.es6";

import { refreshConsentData } from "./declarationOfConsent.js";

var ALLOWED_ORIGINS = [
  "https://docms-int.i.mercedes-benz.com",
  "https://docms.i.mercedes-benz.com",
];

var APPLICATION_VERSION = "2.0.2";

var listenerActive = false;
var loginStateCheckTimer;
var previousLoginState;
var LOGIN_STATE_KEY = "ow_aem_loginstate";
var PREVIOUS_LOGIN_STATE_KEY = "ow_aem_prev_loginstate";

function readPersistedPreviousLoginState() {
  try {
    var persisted = window.sessionStorage.getItem(PREVIOUS_LOGIN_STATE_KEY);
    return persisted === null ? previousLoginState : persisted;
  } catch (e) {
    return previousLoginState;
  }
}

function persistPreviousLoginState(state) {
  previousLoginState = state;
  try {
    if (state === null || state === undefined) {
      window.sessionStorage.removeItem(PREVIOUS_LOGIN_STATE_KEY);
    } else {
      window.sessionStorage.setItem(PREVIOUS_LOGIN_STATE_KEY, state);
    }
  } catch (e) {
    logIfDebugEnabled("[OneDoC] Failed to persist previous login state:", e);
  }
}

function clearSessionDataOnLogout() {
  var currentLoginState;
  try {
    currentLoginState = window.sessionStorage.getItem(LOGIN_STATE_KEY);
  } catch (e) {
    logIfDebugEnabled("[OneDoC] Failed to read login state:", e);
    return;
  }

  var lastLoginState = readPersistedPreviousLoginState();

  if (
    currentLoginState === "logged_out" &&
    lastLoginState === "logged_in"
  ) {
    logIfDebugEnabled("[OneDoC] Logout detected — clearing login session data");
    try {
      clearLoginSessionData();
    } catch (e) {
      logIfDebugEnabled("[OneDoC] Failed to clear login session data:", e);
    }
  }

  persistPreviousLoginState(currentLoginState);
}

/**
 * Seed the persisted previous login state without overwriting an existing
 * value, so a logout that happened during a page reload is still detected.
 */
function seedPreviousLoginState() {
  try {
    var persisted = window.sessionStorage.getItem(PREVIOUS_LOGIN_STATE_KEY);
    if (persisted !== null) {
      previousLoginState = persisted;
      return;
    }
    var currentLoginState = window.sessionStorage.getItem(LOGIN_STATE_KEY);
    previousLoginState = currentLoginState;
    if (currentLoginState !== null) {
      window.sessionStorage.setItem(PREVIOUS_LOGIN_STATE_KEY, currentLoginState);
    }
  } catch (e) {
    previousLoginState = undefined;
    logIfDebugEnabled("[OneDoC] Failed to seed previous login state:", e);
  }
}

/**
 * Build the consent map keyed by lowercased scope name, each value being boolean.
 * Supports both the current map shape and the legacy scope array shape.
 */
export function buildConsentMap(consentStatus) {
  var consent = {};

  if (Array.isArray(consentStatus)) {
    return consentStatus.reduce(function (mappedConsent, scope) {
      if (scope && scope.name) {
        mappedConsent[String(scope.name).toLowerCase()] = !!scope.agreed;
      }
      return mappedConsent;
    }, consent);
  }

  if (consentStatus && typeof consentStatus === "object") {
    Object.keys(consentStatus).forEach(function (scopeName) {
      consent[String(scopeName).toLowerCase()] = !!consentStatus[scopeName];
    });
  }

  return consent;
}

/**
 * Push a OneDoC consent tracking event to the AEM data layer (dataLayer_ow).
 * The postMessage listener pushes form_submission events for callbacks.
 * The login flow pushes login_flow only when stored consent data is first
 * populated or changes.
 */
export function pushConsentTrackingEvent(fullResponse, eventLabel) {
  try {
    var consentStatus = (fullResponse && fullResponse.consentStatus) || [];

    var trackingEvent = {
      event: "click",
      eventDetail: {
        name: "retrieve_consent",
        category: "impression",
        action: "check_consent",
        label: eventLabel,
      },
      user: {
        firstName: (fullResponse && fullResponse.firstName) || "",
        lastName: (fullResponse && fullResponse.lastName) || "",
        email: (fullResponse && fullResponse.email) || "",
        phoneNumber: (fullResponse && fullResponse.phoneNumber) || "",
      },
      consentStatus: buildConsentMap(consentStatus),
      application: {
        id: "privacy_center",
        version: APPLICATION_VERSION,
      },
    };

    window.dataLayer_ow = window.dataLayer_ow || [];
    logIfDebugEnabled(
      "[OneDoC] [DATALAYER] About to push consent tracking event to window.dataLayer_ow, label:",
      eventLabel,
    );
    window.dataLayer_ow.push(trackingEvent);
    logIfDebugEnabled(
      "[OneDoC] [DATALAYER] Pushed tracking event to dataLayer_ow, queue length:",
      window.dataLayer_ow.length,
      "event:",
      JSON.stringify(trackingEvent),
    );
  } catch (e) {
    logIfDebugEnabled(
      "[OneDoC] [DATALAYER] ERROR: Failed to push tracking event to dataLayer_ow:",
      e,
    );
  }
}

/**
 * Build the full response object from DoC MS callback data.
 * Maps all available fields: result, resultMessage, consentStatus,
 * and contact info (first name, last name, email, phone, address, country, CIAM, campaign).
 */
function buildFullResponse(callbackType, data) {
  var response = {
    callbackType: callbackType,
    result: data.result || "",
    resultMessage: data.resultMessage || "",
    consentStatus: {},
  };

  if (data.consentStatus) {
    response.consentStatus = buildConsentMap(data.consentStatus);
  }

  if (data.ct) response.customerType = data.ct;
  if (data.tn) response.phoneNumber = data.tn;
  if (data.tt) response.phoneType = data.tt;
  if (data.ea) response.email = data.ea;
  if (data.ah) response.houseNumber = data.ah;
  if (data.as) response.street = data.as;
  if (data.azip) response.postalCode = data.azip;
  if (data.al) response.city = data.al;
  if (data.country) response.country = data.country;
  if (data.ciam) response.ciamId = data.ciam;
  if (data.cpid) response.campaignId = data.cpid;
  if (data.pnf) response.firstName = data.pnf;
  if (data.pnl) response.lastName = data.pnl;

  return response;
}

/**
 * Store consent callback result in sessionStorage.
 * Always builds and stores the full response for every callback type.
 */
function storeResult(callbackType, data) {
  logIfDebugEnabled("[OneDoC] [LISTENER] Storing callback result:", callbackType);

  try {
    logIfDebugEnabled("[OneDoC] [DOCMS-RESPONSE]", JSON.stringify(data));
    var fullResponse = buildFullResponse(callbackType, data);

    if (callbackType === "spaErrorCallback") {
      Object.keys(fullResponse.consentStatus).forEach(function (scopeName) {
        fullResponse.consentStatus[scopeName] = false;
      });
    }

    var hasAgreed = Object.keys(fullResponse.consentStatus).some(
      function (scopeName) {
        return fullResponse.consentStatus[scopeName];
      },
    );

    logIfDebugEnabled(
      "[OneDoC] [LISTENER] Full response mapped:",
      JSON.stringify(fullResponse),
    );

    window.sessionStorage.setItem(
      ME_SESSION_STORAGE_KEYS.dataUsageScopes,
      JSON.stringify(fullResponse),
    );

    if (callbackType === "spaCompletedCallback") {
      window.sessionStorage.setItem(
        ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStamp,
        JSON.stringify(Date.now()),
      );
      window.sessionStorage.removeItem(
        ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStampFailed,
      );
      window.sessionStorage.setItem(
        ME_SESSION_STORAGE_KEYS.declarationOfConsent,
        JSON.stringify(!!hasAgreed),
      );
    }

    if (callbackType === "spaSkippedCallback") {
      window.sessionStorage.setItem(
        ME_SESSION_STORAGE_KEYS.declarationOfConsent,
        JSON.stringify(!!hasAgreed),
      );
    }

    if (callbackType === "spaErrorCallback") {
      window.sessionStorage.removeItem(
        ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStamp,
      );
      window.sessionStorage.setItem(
        ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStampFailed,
        JSON.stringify(Date.now()),
      );
      window.sessionStorage.setItem(
        ME_SESSION_STORAGE_KEYS.declarationOfConsent,
        JSON.stringify(false),
      );
    }

    pushConsentTrackingEvent(fullResponse, "form_submission");
  } catch (e) {
    logIfDebugEnabled(
      "[OneDoC] [LISTENER] Failed to write result to sessionStorage:",
      e,
    );
  }
}

/**
 * Internal handler for window 'message' events from DoC MS origins.
 */
function handleDocmsMessage(event) {
  if (ALLOWED_ORIGINS.indexOf(event.origin) === -1) {
    return;
  }

  logIfDebugEnabled(
    "[OneDoC] [LISTENER] Received postMessage from DoC MS origin:",
    event.origin,
  );

  var raw = event.data;
  if (!raw || typeof raw !== "string") return;

  logIfDebugEnabled("[OneDoC] [LISTENER] Raw message data:", raw);

  var start = raw.indexOf("(");
  var end = raw.lastIndexOf(")");

  if (start === -1) {
    var method = raw.replace("()", "");
    if (method === "thankYouEvent") {
      logIfDebugEnabled("[OneDoC] [LISTENER] Callback: thankYouEvent received");
    }
    return;
  }

  if (end === -1) return;

  var methodName = raw.substring(0, start);
  var rawPayload = raw.substring(start + 1, end);

  if (!rawPayload || rawPayload.trim() === "") return;

  var payload;
  try {
    payload = JSON.parse(rawPayload);
  } catch (e) {
    logIfDebugEnabled("[OneDoC] [LISTENER] Could not parse message:", methodName);
    return;
  }

  switch (methodName) {
    case "spaCompletedCallback":
      logIfDebugEnabled(
        "[OneDoC] [LISTENER] Callback: spaCompletedCallback — consent saved, result:",
        payload.result,
      );
      logIfDebugEnabled(
        "[OneDoC] [LISTENER] Consent status:",
        JSON.stringify(payload.consentStatus),
      );
      storeResult("spaCompletedCallback", payload);
      logIfDebugEnabled(
        "[OneDoC] [LISTENER] Data written to sessionStorage key: ow_aem_dataUsageScopes",
      );
      break;
    case "spaSkippedCallback":
      logIfDebugEnabled(
        "[OneDoC] [LISTENER] Callback: spaSkippedCallback — consent skipped, result:",
        payload.result,
      );
      storeResult("spaSkippedCallback", payload);
      logIfDebugEnabled(
        "[OneDoC] [LISTENER] Existing consent scopes preserved in sessionStorage",
      );
      break;
    case "spaErrorCallback":
      logIfDebugEnabled(
        "[OneDoC] [LISTENER] Callback: spaErrorCallback — error occurred:",
        payload.resultMessage,
      );
      storeResult("spaErrorCallback", payload);
      logIfDebugEnabled(
        "[OneDoC] [LISTENER] Failed timestamp written to sessionStorage",
      );
      break;
    case "setIframeHeight":
      logIfDebugEnabled(
        "[OneDoC] [LISTENER] Callback: setIframeHeight — iframe height adjustment",
      );
      break;
    default:
      logIfDebugEnabled("[OneDoC] [LISTENER] Unknown callback method:", methodName);
      break;
  }
}

/**
 * Refresh consent state after Privacy Center persists a toggle.
 * Triggers the existing DoCMS backend retrieval flow (declarationOfConsent.js)
 * so sessionStorage/dataLayer reflect the latest data from DoCMS, instead of
 * only the values the frontend just sent.
 */
function handleConsentUpdated(event) {
  try {
    var detail = event && event.detail;
    var hasConsentValues =
      detail &&
      ((Array.isArray(detail.consents) &&
        detail.consents.some(function (consent) {
          return consent && consent.consentId;
        })) ||
        detail.consentId);

    if (!hasConsentValues) {
      logIfDebugEnabled(
        "[OneDoC] [LISTENER] Ignoring consent update without any consent values",
      );
      return;
    }

    logIfDebugEnabled(
      "[OneDoC] [LISTENER] Triggering DoCMS consent refresh after Privacy Center save",
    );
    refreshConsentData();
  } catch (e) {
    logIfDebugEnabled("[OneDoC] [LISTENER] Failed to trigger consent refresh:", e);
  }
}

/**
 * Start listening for DoC MS postMessage callbacks.
 * Safe to call multiple times — only one listener is registered.
 * Must be initialized before OneDoC SPA loads.
 */
export function initDocmsListener() {
  if (listenerActive) {
    logIfDebugEnabled(
      "[OneDoC] [LISTENER] Global OneDoC listener already active — skipping duplicate registration",
    );
    return;
  }
  window.addEventListener("message", handleDocmsMessage);
  window.addEventListener("onedoc:consentUpdated", handleConsentUpdated);
  seedPreviousLoginState();
  loginStateCheckTimer = window.setInterval(clearSessionDataOnLogout, 1000);
  listenerActive = true;
  logIfDebugEnabled(
    "[OneDoC] [LISTENER] Global OneDoC listener initialized — listening for postMessage from:",
    ALLOWED_ORIGINS.join(", "),
  );
  logIfDebugEnabled(
    "[OneDoC] [LISTENER] Handling callbacks: spaCompletedCallback, spaSkippedCallback, spaErrorCallback, setIframeHeight, thankYouEvent",
  );
}

/**
 * Stop listening for DoC MS postMessage callbacks.
 */
export function removeDocmsListener() {
  if (!listenerActive) return;
  window.removeEventListener("message", handleDocmsMessage);
  window.removeEventListener("onedoc:consentUpdated", handleConsentUpdated);
  window.clearInterval(loginStateCheckTimer);
  loginStateCheckTimer = undefined;
  listenerActive = false;
  logIfDebugEnabled("[OneDoC] [LISTENER] Global OneDoC listener removed");
}

/**
 * Read the latest dataUsageScopes from sessionStorage.
 * Returns null if no result has been stored yet.
 */
export function getDataUsageScopes() {
  try {
    var stored = window.sessionStorage.getItem(
      ME_SESSION_STORAGE_KEYS.dataUsageScopes,
    );
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (e) {
    return null;
  }
}

export function clearLoginSessionData() {
  clearDataUsageScopes();
  window.sessionStorage.removeItem(
    ME_SESSION_STORAGE_KEYS.dataUsageScopesIdentity,
  );
  window.sessionStorage.removeItem(DEBUG_SESSION_KEY);
}

/**
 * Clear the dataUsageScopes from sessionStorage.
 */
export function clearDataUsageScopes() {
  window.sessionStorage.removeItem(ME_SESSION_STORAGE_KEYS.dataUsageScopes);
  window.sessionStorage.removeItem(
    ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStamp,
  );
  window.sessionStorage.removeItem(
    ME_SESSION_STORAGE_KEYS.dataUsageScopesTimeStampFailed,
  );
  window.sessionStorage.removeItem(
    ME_SESSION_STORAGE_KEYS.declarationOfConsent,
  );
}
